import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";

interface HomeRow {
  id: number; name: string; address: string; sdaType: string;
  status: string; roomCount: number; occupied: number; vacant: number;
}

const field = "input";
const SDA_TYPES = ["basic", "improved_liveability", "fully_accessible", "robust", "high_physical_support"];

export default function Homes() {
  const { data, error, loading, reload } = useFetch<HomeRow[]>("/v1/sil/homes");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", sdaType: "improved_liveability", bathrooms: "1", parking: "0" });
  const [formError, setFormError] = useState<string | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    try {
      await api("/v1/sil/homes", { method: "POST", body: JSON.stringify({
        name: form.name, address: form.address, sdaType: form.sdaType,
        bathrooms: Number(form.bathrooms), parking: Number(form.parking),
      }) });
      setForm({ name: "", address: "", sdaType: "improved_liveability", bathrooms: "1", parking: "0" });
      setShowForm(false); setFormError(null); reload();
    } catch (err) { setFormError((err as Error).message); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink">Homes (SIL)</h1>
        <button onClick={() => setShowForm((v) => !v)} className="btn-primary">
          {showForm ? "Cancel" : "Add home"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="card p-5 grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
          <label className="block sm:col-span-2"><span className="label">Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} required /></label>
          <label className="block sm:col-span-2"><span className="label">Address</span>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={field} required /></label>
          <label className="block"><span className="label">SDA type</span>
            <select value={form.sdaType} onChange={(e) => setForm({ ...form, sdaType: e.target.value })} className={field}>
              {SDA_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="label">Baths</span>
              <input type="number" min="0" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className={field} /></label>
            <label className="block"><span className="label">Parking</span>
              <input type="number" min="0" value={form.parking} onChange={(e) => setForm({ ...form, parking: e.target.value })} className={field} /></label>
          </div>
          <button className="btn-dark">Save</button>
          {formError && <p className="text-sm text-red-600 sm:col-span-6">{formError}</p>}
        </form>
      )}

      {loading && <div className="space-y-3"><div className="skeleton h-24" /><div className="skeleton h-24" /></div>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {data && data.length === 0 && <p className="text-mut">No homes yet. Add your first SIL property.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(data ?? []).map((h) => (
          <Link key={h.id} to={`/homes/${h.id}`} className="card p-5 hover:border-teal-300 transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-base font-semibold text-ink">{h.name}</p>
                <p className="text-sm text-mut">{h.address}</p>
              </div>
              <span className="chip bg-teal-50 text-teal-700 capitalize">{h.sdaType.replace(/_/g, " ")}</span>
            </div>
            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="text-body font-medium">{h.roomCount} room{h.roomCount === 1 ? "" : "s"}</span>
              <span className="text-teal-700">{h.vacant} vacant</span>
              <span className="text-mut">{h.occupied} occupied</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
